import { createSlice } from "@reduxjs/toolkit";

/**
 * TIP-511 — localStorage keys that belong to ONE signed-in user.
 *
 * Deliberately an explicit allow-list rather than `localStorage.clear()`: a
 * routine token expiry must not destroy an in-progress flow, so this list is
 * only applied when the identity actually CHANGES (user A's session replaced by
 * user B on the same browser). Machine-scoped keys — `cookieConsent`,
 * `appvalue`, `lastVisited` — are intentionally absent and survive.
 */
export const IDENTITY_SCOPED_STORAGE_KEYS = [
  // session / credentials
  "isLoggedIn",
  "jwt",
  "Access_token",
  "userInfo",
  "loginTime",
  "keepMe",
  // who the user is / which company they act for
  "userindividualID",
  "userSelectedCompany",
  "currentEditingCompany",
  "filledcompany",
  // company + industry caches
  "companyData",
  "companyAddress",
  "companyDescription",
  "industryData",
  "industryDetails",
  "authorizedSignatory",
  // purchase / checkout context (the TIP-493 "N4" leak)
  "payasyougoCompanyID",
  "paycompanyStatus",
  "webProfilePurchaseMode",
  "UpgradePlan",
  "billing_add_ID",
  "slug",
  // in-progress intake flows
  "createCompany",
  "createWorldbox",
  "createWorldboxCompany",
  "worldboxData",
  "insuranceData",
  "financialData",
  "uploadData",
  "invite_code",
  // claim flow
  "claimAuthSignDetails",
  "claimTradeLicenceDetails",
  "claimContactDetails",
  "claimAddressDetails",
  // transient UI position within those flows
  "CurrentComponent",
  "accordianIndex",
  "selectedObject",
  "state",
];

/**
 * TIP-511 — drop every identity-scoped key without touching anything else.
 * Exported so non-reducer call sites (and tests) can reuse the one list.
 */
export const clearIdentityScopedStorage = () => {
  IDENTITY_SCOPED_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

const initialState = {
  isLoggedIn: localStorage.getItem("isLoggedIn")
    ? JSON.parse(localStorage.getItem("isLoggedIn"))
    : false,
  userInfo: null,
  token: null,
  isModalVisible: false,
  expireTime: null,
  showLoginModal: false,
  showIdleModal: false,
  modalType: null,
  lastRoute: null,
  // TIP-511 — email of the identity that owned the session when it was
  // invalidated (401). Drives the locked email field in the "Login Required"
  // modal and the mismatch check on submit. Redux-only, never persisted.
  pendingReauthEmail: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setToken: (state, action) => {
      state.token = action.payload;
      // localStorage.setItem("jwt", JSON.stringify(action.payload));
    },
    setUserInfo: (state, action) => {
      state.userInfo = action.payload;
      state.isLoggedIn = true;
      localStorage.setItem("isLoggedIn", true);
      // localStorage.setItem("userInfo", JSON.stringify(action.payload));
    },
    showModal: (state) => {
      state.isModalVisible = true;
    },
    hideModal: (state) => {
      state.isModalVisible = false;
    },
    logout: (state) => {
      state.token = null;
      state.refresh = null;
      state.user = null;
      state.expireTime = null;

      state.showLoginModal = false;
      state.showIdleModal = false;
      state.lastRoute = null;
      state.pendingReauthEmail = null;
    },

    // clearUserInfo: (state) => {
    //   state.user = null;
    // },

    // ------------------------
    // Login Modal
    // ------------------------

    showLoginModal: (state) => {
      state.showLoginModal = true;
    },

    hideLoginModal: (state) => {
      state.showLoginModal = false;
    },

    // ------------------------
    // Re-auth identity (TIP-511)
    // ------------------------

    /**
     * Remember who the invalidated session belonged to, so the login modal can
     * pin the email field to that account. Normalised to match the lowercasing
     * `handleLogin` applies before it hits the API.
     */
    setPendingReauthIdentity: (state, action) => {
      const email = action.payload;
      state.pendingReauthEmail = email ? String(email).toLowerCase() : null;
    },

    clearPendingReauthIdentity: (state) => {
      state.pendingReauthEmail = null;
    },

    /**
     * A different user has taken over this browser. Drop the previous
     * identity's cached state — but only the identity-scoped keys, so a
     * same-user re-login keeps its in-progress work.
     */
    clearIdentityScopedState: (state) => {
      state.pendingReauthEmail = null;
      clearIdentityScopedStorage();
    },

    // ------------------------
    // Idle / Session Modal
    // ------------------------

    showIdleModal: (state, action) => {
      state.showIdleModal = true;
      state.modalType = action.payload;
    },

    hideIdleModal: (state) => {
      state.showIdleModal = false;
    },

    // ------------------------
    // Session Expiry
    // ------------------------

    setExpireTime: (state, action) => {
      state.expireTime = action.payload;
    },

    clearExpireTime: (state) => {
      state.expireTime = null;
    },

    // ------------------------
    // Resume Route
    // ------------------------

    saveLastRoute: (state, action) => {
      state.lastRoute = action.payload;
    },
    clearUserInfo: (state) => {
      state.userInfo = null;
      state.token = null;
      state.isLoggedIn = false;
      state.expireTime = null;
      state.showLoginModal = false;
      state.showIdleModal = false;
      state.lastRoute = null;
      state.pendingReauthEmail = null;
      const otp = localStorage.getItem('appvalue');
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("userInfo");
      localStorage.removeItem("jwt");
      localStorage.removeItem("companyData");
      localStorage.removeItem("industryData");
      localStorage.removeItem("userSelectedCompany");
      localStorage.removeItem("authorizedSignatory");
      localStorage.removeItem("companyAddress");
      localStorage.removeItem("userindividualID");
      localStorage.removeItem("invite_code");
      localStorage.clear();
      if (otp) {
        localStorage.setItem('appvalue', otp);
      }
    },
  },
});

export const { setUserInfo, clearUserInfo, setToken, showModal, hideModal, logout, // Login Modal
  showLoginModal,
  hideLoginModal,

  // Re-auth identity (TIP-511)
  setPendingReauthIdentity,
  clearPendingReauthIdentity,
  clearIdentityScopedState,

  // Idle Modal
  showIdleModal,
  hideIdleModal,

  // Session
  setExpireTime,
  clearExpireTime,

  // Navigation
  saveLastRoute, } = authSlice.actions;

export default authSlice.reducer;
